/**
 * Convert an `HH:mm` time string into hour and minute components.
 * Throws on invalid input.
 */
export function hhmmToHourMinute(hhmm) {
    if (!/^\d{2}:\d{2}$/.test(hhmm)) {
        throw new Error(`Invalid HH:mm time: "${hhmm}"`);
    }
    const parts = hhmm.split(":").map(Number);
    const hour = parts[0];
    const minute = parts[1];
    if (hour > 23 || minute > 59) {
        throw new Error(`Invalid HH:mm time: "${hhmm}"`);
    }
    return { hour, minute };
}
/**
 * Validate that a string is a valid `HH:mm` time
 * (e.g. rejects `"25:00"` but accepts `"08:30"`).
 */
export function isValidHhmm(hhmm) {
    try {
        hhmmToHourMinute(hhmm);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Compare two `HH:mm` strings.
 * Returns negative if `a < b`, `0` if equal, positive if `a > b`.
 */
export function compareHhmm(a, b) {
    const pa = hhmmToHourMinute(a);
    const pb = hhmmToHourMinute(b);
    return pa.hour * 60 + pa.minute - (pb.hour * 60 + pb.minute);
}
/**
 * Convert hour and minute numbers to an `HH:mm` string.
 * Throws on non-integer or out-of-range inputs.
 */
export function hourMinuteToHhmm(hour, minute) {
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
        throw new TypeError(`hour and minute must be finite integers, got hour=${hour}, minute=${minute}`);
    }
    if (hour < 0 || hour > 23) {
        throw new RangeError(`hour must be between 0 and 23, got ${hour}`);
    }
    if (minute < 0 || minute > 59) {
        throw new RangeError(`minute must be between 0 and 59, got ${minute}`);
    }
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}
/**
 * Add hours to an `HH:mm` time string, capped at `23:59`.
 */
export function addHoursToHhmm(hhmm, hours) {
    if (!Number.isInteger(hours) || hours < 0) {
        throw new RangeError(`hours must be a non-negative integer, got ${hours}`);
    }
    const { hour, minute } = hhmmToHourMinute(hhmm);
    if (hour + hours >= 24) {
        return "23:59";
    }
    return hourMinuteToHhmm(hour + hours, minute);
}
